/**
 * Lightweight diagnostics — runtime console ring buffer + snapshot builder.
 *
 * Used by VersionStamp's "Send diagnostic snapshot" debug action to give
 * users a one-tap way to email a useful bug report. Captures only
 * non-sensitive runtime context — no auth tokens, no body content, no
 * PII other than what the user types into their email.
 *
 * The console interceptor wraps console.log/info/warn/error to push into
 * a fixed-size ring (newest entries kick oldest). Calls are still
 * forwarded to the original console methods so dev experience is
 * unchanged.
 */
import { APP_VERSION } from './appVersion';

const RING_SIZE = 30;
const ring = [];
let installed = false;

const safeStringify = (val) => {
  if (val === undefined) return 'undefined';
  if (val === null) return 'null';
  if (typeof val === 'string') return val;
  if (val instanceof Error) return `${val.name}: ${val.message}`;
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

const push = (level, args) => {
  if (ring.length >= RING_SIZE) ring.shift();
  ring.push({
    t: new Date().toISOString(),
    level,
    msg: Array.from(args).map(safeStringify).join(' ').slice(0, 400),
  });
};

/**
 * Install once at app boot. Idempotent — repeat calls do nothing.
 */
export function initConsoleCapture() {
  if (installed || typeof window === 'undefined' || !window.console) return;
  installed = true;
  const orig = {
    log: console.log.bind(console),
    info: console.info ? console.info.bind(console) : console.log.bind(console),
    warn: console.warn ? console.warn.bind(console) : console.log.bind(console),
    error: console.error ? console.error.bind(console) : console.log.bind(console),
  };
  ['log', 'info', 'warn', 'error'].forEach((level) => {
    console[level] = (...args) => {
      try { push(level, args); } catch { /* never break logging */ }
      orig[level](...args);
    };
  });
  // Capture uncaught errors and unhandled rejections too — these are
  // exactly the cases users would otherwise be unable to describe.
  window.addEventListener('error', (e) => {
    push('error', [`window.onerror: ${e.message} at ${e.filename}:${e.lineno}`]);
  });
  window.addEventListener('unhandledrejection', (e) => {
    push('error', [`unhandledrejection: ${safeStringify(e.reason)}`]);
  });
}

const isStandalonePwa = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch { /* ignore */ }
  // iOS Safari historical flag
  return Boolean(window.navigator && window.navigator.standalone);
};

/**
 * Build a human-readable snapshot string. Plain text so it pastes into
 * any mail client / chat without rendering issues.
 */
export function getDiagnosticSnapshot() {
  if (typeof window === 'undefined') return 'No diagnostics available in this environment.';

  const lines = [];
  lines.push('--- NeuroWave Diagnostic Snapshot ---');
  lines.push(`Captured: ${new Date().toISOString()}`);
  lines.push(`App version: ${APP_VERSION}`);
  lines.push(`URL: ${window.location.href}`);
  lines.push('');

  lines.push('## Environment');
  lines.push(`User agent: ${navigator.userAgent}`);
  lines.push(`Platform: ${navigator.platform || 'unknown'}`);
  lines.push(`Language: ${navigator.language || 'unknown'}`);
  lines.push(`Online: ${navigator.onLine}`);
  lines.push(`Standalone PWA: ${isStandalonePwa()}`);
  try {
    const hover = window.matchMedia('(hover: hover)').matches;
    const fine = window.matchMedia('(pointer: fine)').matches;
    lines.push(`Hover capable: ${hover}, Pointer fine: ${fine}`);
  } catch { /* ignore */ }
  lines.push(`Viewport: ${window.innerWidth}×${window.innerHeight} @ DPR ${window.devicePixelRatio || 1}`);
  lines.push('');

  lines.push('## Service worker');
  if ('serviceWorker' in navigator) {
    lines.push(`Controller: ${navigator.serviceWorker.controller ? 'active' : 'none'}`);
  } else {
    lines.push('Not supported');
  }
  lines.push('');

  lines.push('## NeuroWave localStorage keys');
  try {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith('neurowave_'));
    if (keys.length === 0) {
      lines.push('(none)');
    } else {
      // Just key names + sizes — never include values (might be sensitive).
      keys.forEach((k) => {
        const v = localStorage.getItem(k) || '';
        lines.push(`- ${k} (${v.length} chars)`);
      });
    }
  } catch {
    lines.push('localStorage unavailable');
  }
  lines.push('');

  lines.push(`## Last ${ring.length} console entries`);
  if (ring.length === 0) {
    lines.push('(no console activity captured)');
  } else {
    ring.forEach((entry) => {
      lines.push(`[${entry.t}] ${entry.level.toUpperCase()}: ${entry.msg}`);
    });
  }
  lines.push('');
  lines.push('--- End snapshot ---');

  return lines.join('\n');
}

export function buildSupportMailto(toEmail) {
  const snapshot = getDiagnosticSnapshot();
  const subject = `NeuroWave bug report — v${APP_VERSION}`;
  const body = [
    'Hi! I hit an issue:',
    '',
    '(Please describe what you were doing and what went wrong.)',
    '',
    '',
    '----',
    'Diagnostic snapshot (please keep this — it helps us fix it faster):',
    '',
    snapshot,
  ].join('\n');
  return `mailto:${encodeURIComponent(toEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
